import { useContext, useState } from 'react';
import ModalComponent from 'components/Common/Modal';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import dayjs from 'dayjs';
import { store } from 'components/Store';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import InteractionFormFields from '../InteractionFormFields';

const LogInteractionModal = ({ onClose, onSuccess, title: modalTitle, preselectedStakeholders = [] }) => {
  const { state } = useContext(store);
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();
  const defaultTitle =
    agencyUser || newAgencyPlan ? `Log an interaction for ${state.team?.teamName}` : 'Log an interaction';
  const { myself } = state.team ?? {};
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    date: dayjs().format('YYYY-MM-DD'),
    type: '',
    description: '',
    initiator: '',
    stakeholders: preselectedStakeholders,
    members: myself ? [{ value: myself.id, id: myself.id, label: myself.fullName ?? myself.emailAddress }] : [],
  });

  const { generalApiCall } = useGeneralApiCall();

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const isFormValid = !!form.title.trim() && !!form.type && !!form.date;

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      createNotification('danger', 'Please enter a title');
      return;
    }
    if (!form.type) {
      createNotification('danger', 'Please select an interaction type');
      return;
    }
    if (!form.date) {
      createNotification('danger', 'Please select a date');
      return;
    }

    setIsSubmitting(true);
    const result = await generalApiCall({
      method: 'post',
      pathname: '/api/crm-interaction/create',
      requestProperties: {
        title: form.title,
        date: form.date,
        type: form.type,
        description: form.description,
        initiatedByOrganisation:
          form.initiator === 'Organisation' ? true : form.initiator === 'Stakeholder' ? false : null,
        contactIds: form.stakeholders.map((p) => p.id),
        userIds: form.members.map((m) => m.value),
      },
      needsAuthentication: true,
    });
    setIsSubmitting(false);

    if (result) {
      createNotification('success', 'Interaction logged successfully');
      onSuccess?.(result);
    }
  };

  return (
    <ModalComponent isOpen={true} setIsOpen={() => onClose()} maxWidth={700} height='90vh' notShowCloseButton={true}>
      <div className='d-flex flex-column h-100'>
        <div className='px-4 py-3 border-bottom flex-centered justify-content-between flex-shrink-0'>
          <h3 className='mb-0 title-h5-m-bold'>{modalTitle ?? defaultTitle}</h3>
          <button className='circle-button general-button hovered-button action-state-hover' onClick={onClose}>
            <span className='icon-close paragraph-p5' />
          </button>
        </div>

        <CustomScrollbar className='simple-scrollbar indented-scrollbar-small flex-grow-1' maximalThumbYSize={200}>
          <div className='px-4 py-4'>
            <InteractionFormFields form={form} onChange={handleChange} />
          </div>
        </CustomScrollbar>

        <div className='px-4 py-3 border-top flex-shrink-0 d-flex justify-content-end'>
          <button
            className='general-button action-button rounded-pill py-2 px-4 paragraph-p2'
            onClick={handleSubmit}
            disabled={isSubmitting || !isFormValid}
          >
            {isSubmitting ? 'Logging...' : 'Log interaction'}
          </button>
        </div>
      </div>
    </ModalComponent>
  );
};

export default LogInteractionModal;
